import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CreditCard, Loader2, AlertCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { paymentCardGradient } from '@/components/payment/PaymentCardGradientStyles';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { deferaCapitalNmiUrl } from '@/lib/apiUrls';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { nmiDeclineMessage } from '@/lib/nmiDeclineMessage';
import { extractNmiCardDisplayFromCollectJs } from '@/utils/nmiCardDisplay';
import { cn } from '@/lib/utils';
import {
  MODAL_GHOST_BTN_CLASS,
  MODAL_PRIMARY_BTN_CLASS,
} from '@/components/booking/paymentScheduleModalStyles';
import {
  NMI_FIELD_SHELL_CLASS,
  nmiCollectParentStyles,
  nmiCollectStyleConfig,
} from '@/components/defera-capital-nmi/nmiCollectFieldStyles';
import { loadCollectJs } from '@/components/defera-capital-nmi/nmiCollectJsLoader';

/**
 * Defera Capital — NMI Restart Plan form.
 *
 * Parallel of `DeferaCapitalRestartPlanForm` (Stripe). Mounted by
 * `PaymentScheduleDisplay` when the booking is on the NMI processor track
 * and the schedule endpoint reports the plan needs a restart
 * (`schedule.plan_restart != null`).
 *
 * Two paths:
 *   1. "Charge the card on file" — single POST to `restart-plan` against
 *      the existing vault. Use this when the failure was transient (NSF,
 *      bank holdback, etc.).
 *   2. "Use a new card" — Collect.js inline iframe collects a one-time
 *      `payment_token`, the form sends BOTH the token and the restart
 *      request in one POST. The edge fn updates the vault first; if
 *      that fails the restart charge is never attempted.
 *
 * No card picker — NMI has a single vault per customer, so the only
 * meaningful choice is "old card vs new card".
 */

export default function DeferaCapitalNmiRestartPlanForm({ tripId, onClose, onSuccess, fmtCurrency, planRestart, onPaymentUncertain }) {
  const [useNewCard, setUseNewCard] = useState(false);
  const [tokenizationKey, setTokenizationKey] = useState(null);
  const [keyLoadError, setKeyLoadError] = useState(null);
  const [collectReady, setCollectReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const onTokenRef = useRef(null);
  const submitLockRef = useRef(false);

  // Lazy-load the tokenization key the first time the user switches to "new card".
  const ensureTokenizationKey = useCallback(async () => {
    if (tokenizationKey) return tokenizationKey;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Please sign in');
    const res = await fetch(deferaCapitalNmiUrl('create-update-card-session'), {
      method: 'POST',
      headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
      body: JSON.stringify({ booking_id: tripId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Could not start card update');
    setTokenizationKey(data.tokenization_key);
    return data.tokenization_key;
  }, [tokenizationKey, tripId]);

  // Configure Collect.js once when the customer opts into the new card path.
  useEffect(() => {
    if (!useNewCard) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const key = await ensureTokenizationKey();
        if (cancelled) return;
        const CollectJS = await loadCollectJs(key);
        if (cancelled) return;
        CollectJS.configure({
          variant: 'inline',
          styleSniffer: false,
          tokenizationKey: key,
          fields: {
            ccnumber: { selector: '#dc-nmi-restart-ccnumber', title: 'Card Number', placeholder: 'Card number' },
            ccexp: { selector: '#dc-nmi-restart-ccexp', title: 'Expiration', placeholder: 'MM / YY' },
            cvv: { display: 'show', selector: '#dc-nmi-restart-cvv', title: 'CVV', placeholder: 'CVC' },
          },
          ...nmiCollectStyleConfig(),
          fieldsAvailableCallback: () => { if (!cancelled) setCollectReady(true); },
          callback: (resp) => { const cb = onTokenRef.current; if (cb) cb(resp); },
        });
      } catch (e) {
        if (!cancelled) setKeyLoadError(e.message || 'Could not load card form');
      }
    })();
    return () => { cancelled = true; };
  }, [useNewCard, ensureTokenizationKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isProcessing || submitLockRef.current) return;
    submitLockRef.current = true;
    setIsProcessing(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in');

      let paymentToken = null;
      let cardDisplay = null;
      if (useNewCard) {
        if (!window.CollectJS || !collectReady) throw new Error('Card form not ready yet');
        const tokenResp = await new Promise((resolve, reject) => {
          onTokenRef.current = (resp) => {
            if (resp?.token) resolve(resp);
            else reject(new Error(resp?.error || 'Tokenization failed'));
          };
          try { window.CollectJS.startPaymentRequest(); }
          catch (ex) { reject(ex); }
        });
        paymentToken = tokenResp.token;
        cardDisplay = extractNmiCardDisplayFromCollectJs(tokenResp);
      }

      const clientRequestId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const res = await fetch(deferaCapitalNmiUrl('restart-plan'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({
          booking_id: tripId,
          payment_token: paymentToken,
          client_request_id: clientRequestId,
          ...(cardDisplay ? { card_display: cardDisplay } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Two-stage failure mode: the edge fn returns `stage: 'update_card'`
        // when the new-card tokenization was rejected by NMI before the
        // restart charge was even attempted. The original vault card is
        // still in place, so we explain that explicitly. Use the shared
        // translator either way — it converts vendor strings like
        // "Do Not Honor" into friendly text.
        const friendly = nmiDeclineMessage(data, {
          defaultMessage: data?.stage === 'update_card'
            ? 'The new card was rejected — your original card is still on file. Try a different card.'
            : "We couldn't restart your plan. Please try again, or update your card first.",
        });
        console.error('[defera-capital-nmi] restart-plan error', {
          rawText: friendly.dev,
          stage: data?.stage,
          error_type: data?.error_type,
          gateway_response: data?.gateway_response,
          gateway_response_code: data?.gateway_response_code,
        });
        const err = new Error(friendly.customer);
        err.nmiAction = friendly.action;
        err.stage = data?.stage || null;
        if (res.status >= 500) onPaymentUncertain?.();
        throw err;
      }
      toast.success('Plan restarted — your schedule is back on track.');
      onSuccess?.();
    } catch (err) {
      const msg = err?.message || 'Could not restart plan';
      setError(msg);
      toast.error(msg, { description: err?.nmiAction || undefined });
      if (err?.name === 'TypeError' || /fetch|network|gateway/i.test(msg)) {
        onPaymentUncertain?.();
      }
    } finally {
      setIsProcessing(false);
      submitLockRef.current = false;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <style>{nmiCollectParentStyles('#dc-nmi-restart-ccnumber, #dc-nmi-restart-ccexp, #dc-nmi-restart-cvv')}</style>
      {/* Past-due summary — same shape as the Stripe variant for visual parity. */}
      <div className="rounded-2xl border border-brand-500/15 bg-brand-500/[0.04] px-4 py-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-brand-100/70 uppercase tracking-widest font-medium">Past-due (charged now)</span>
          <span className="text-base text-white font-normal tabular-nums">${fmtCurrency?.(planRestart?.past_due_cents || 0) ?? '0.00'}</span>
        </div>
        {planRestart?.new_num_payments && planRestart?.new_per_payment_cents && (
          <>
            <div className="h-px bg-white/[0.06]" />
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/60 uppercase tracking-widest font-normal">Resumed schedule</span>
              <span className="text-base text-white font-normal tabular-nums">
                {planRestart.new_num_payments}× ${fmtCurrency?.(planRestart.new_per_payment_cents) ?? '0.00'}
              </span>
            </div>
          </>
        )}
        {planRestart?.first_payment_date ? (
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/50 uppercase tracking-widest font-normal">First installment</span>
            <span className="text-sm text-white/85 font-normal">
              {format(parseISO(planRestart.first_payment_date), 'MMM d, yyyy')}
            </span>
          </div>
        ) : null}
      </div>

      {/* Card selector — single-vault model, so it's just a "same card / new card" toggle. */}
      <div className={paymentCardGradient.container}>
        <p className="text-white/70 text-xs font-normal uppercase tracking-wider mb-3">Charge which card?</p>
        <div className="space-y-2.5">
          <button
            type="button"
            onClick={() => setUseNewCard(false)}
            className={`${paymentCardGradient.cardRowBase} ${!useNewCard ? paymentCardGradient.cardSelected : paymentCardGradient.cardUnselected}`}
          >
            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${!useNewCard ? paymentCardGradient.iconSelected : paymentCardGradient.iconUnselected}`}>
              <CreditCard className={`w-5 h-5 ${!useNewCard ? paymentCardGradient.iconColorSelected : paymentCardGradient.iconColorUnselected}`} />
            </div>
            <div className="flex-1 text-left">
              <span className="font-medium text-white">Card on file</span>
              <span className="text-white/50 font-normal text-sm ml-2">(used at booking)</span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setUseNewCard(true)}
            className={`${paymentCardGradient.cardRowBase} ${useNewCard ? paymentCardGradient.cardSelected : paymentCardGradient.cardUnselected}`}
          >
            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${useNewCard ? paymentCardGradient.iconSelected : paymentCardGradient.iconUnselected}`}>
              <CreditCard className={`w-5 h-5 ${useNewCard ? paymentCardGradient.iconColorSelected : paymentCardGradient.iconColorUnselected}`} />
            </div>
            <div className="flex-1 text-left">
              <span className="font-medium text-white">Use a new card</span>
            </div>
          </button>
        </div>
        {useNewCard && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="text-[11px] text-white/40 uppercase tracking-wider block mb-1.5">Card Number</label>
              <div className={NMI_FIELD_SHELL_CLASS}>
                <div id="dc-nmi-restart-ccnumber" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-white/40 uppercase tracking-wider block mb-1.5">Expiration</label>
                <div className={NMI_FIELD_SHELL_CLASS}>
                  <div id="dc-nmi-restart-ccexp" />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-white/40 uppercase tracking-wider block mb-1.5">CVV</label>
                <div className={NMI_FIELD_SHELL_CLASS}>
                  <div id="dc-nmi-restart-cvv" />
                </div>
              </div>
            </div>
            {keyLoadError && (
              <p className="text-red-400 text-xs">{keyLoadError}</p>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
          <p className="text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        </div>
      )}

      <div className="flex gap-2.5">
        <Button type="button" variant="ghost" onClick={onClose} className={cn(MODAL_GHOST_BTN_CLASS, 'flex-1')}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isProcessing || (useNewCard && !collectReady)}
          className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1')}
        >
          {isProcessing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Restarting…</>) : 'Restart Plan'}
        </Button>
      </div>
    </form>
  );
}
